import { useOptionalAuthState } from '@/app/providers/auth'
import { useCountry } from '@/components/Link'
import type { SplitKey } from '@/constants/splits'
import { SPLITS } from '@/constants/splits'
import useTenant from '@/hooks/useTenant'
import type { EvalContext } from '@caterdesk/flags-react'
import {
  useFeatureFlag as useFlagCore,
  useFeatureFlags as useFlagsCore,
} from '@caterdesk/flags-react'

export { SPLITS }
export type { SplitKey }

type Attributes = NonNullable<EvalContext['attributes']>

// Vendors and admins viewing another account's order pass that accountId at the call site;
// call-site attributes win (PT-2267).
const useAuthAttributes = (): Attributes => {
  const authState = useOptionalAuthState()
  if (authState?.type !== 'authenticated') return {}
  const accountId = authState.user.account?.id
  return { userId: authState.user.id, ...(accountId != null && { accountId }) }
}

// `userId` is the SDK's identity field, not an attribute. Targeting rules and
// rollout bucketing read it from the root of the context, so we move it there.
const toEvalContext = (
  authAttributes: Attributes,
  attributes: Attributes,
  tenantId: string,
  country: string,
): EvalContext => {
  // Nullish call-site values (e.g. an order whose account is not loaded yet) must not clobber
  // the auth-derived ones, so callers can pass `{ accountId: order.account?.id }` directly.
  const merged: Attributes = { ...authAttributes }
  for (const [key, value] of Object.entries(attributes)) {
    if (value != null) merged[key] = value
  }
  const { userId, ...rest } = merged
  return {
    userId: userId == null || userId === '' ? undefined : String(userId),
    attributes: { ...rest, tenantId, country },
  }
}

export const useFeatureFlag = (flagKey: SplitKey, attributes: Attributes = {}): boolean => {
  const tenant = useTenant()
  const country = useCountry()
  const authAttributes = useAuthAttributes()
  return useFlagCore(flagKey, toEvalContext(authAttributes, attributes, tenant.id, country))
}

export const useFeatureFlags = (
  flags: string[],
  attributes: Attributes = {},
): Record<string, boolean> => {
  const tenant = useTenant()
  const country = useCountry()
  const authAttributes = useAuthAttributes()
  return useFlagsCore(flags, toEvalContext(authAttributes, attributes, tenant.id, country))
}
